import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';
import { getMainImage } from '../../../utils/image-utils';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface OrderItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  color?: string;
  size?: string;
  image?: string;
}

interface Order {
  id: number;
  order_code: string;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  payment_method: string;
  payment_type?: string;
  installments?: string;
  items: OrderItem[];
  subtotal: number;
  shipping_cost: number;
  total: number;
  status: string;
  created_at: string;
  status_changed_at?: string;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'shipped', label: 'Enviado' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'cancelled', label: 'Cancelado' },
];

function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useNotification();
  
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadOrder(parseInt(id));
    }
  }, [id]);

  const loadOrder = async (orderId: number) => {
    try {
      const orderData = await AdminAPI.get(`orders/${orderId}`) as Order;
      setOrder(orderData);
      setNewStatus(orderData.status);
    } catch (err: any) {
      console.error('Erro ao carregar pedido:', err);
      error('Erro ao carregar pedido');
      setTimeout(() => navigate('/admin/pedidos'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!order || !newStatus || newStatus === order.status) return;
    
    setUpdating(true);
    try {
      await AdminAPI.put(`orders/${order.id}`, { status: newStatus });
      success('Status do pedido atualizado com sucesso!');
      await loadOrder(order.id);
    } catch (err: any) {
      console.error('Erro ao atualizar status:', err);
      error(err.message || 'Erro ao atualizar status do pedido');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'pending',
      confirmed: 'processing',
      shipped: 'processing',
      delivered: 'delivered',
      cancelled: 'pending',
    };
    return statusMap[status] || 'pending';
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'Pendente',
      confirmed: 'Confirmado',
      shipped: 'Enviado',
      delivered: 'Entregue',
      cancelled: 'Cancelado',
    };
    return statusMap[status] || status;
  };

  return (
    <>
      {/* Loading Spinner - aparece apenas durante carregamento inicial */}
      {loading && <LoadingSpinner message="Carregando detalhes do pedido..." />}
      
      {!loading && !order && (
        <div className="mobile-content" style={{ padding: '20px', textAlign: 'center' }}>
          <div>Pedido não encontrado</div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/admin/pedidos')}
            style={{ marginTop: '16px' }}
          >
            Voltar
          </button>
        </div>
      )}

      {!loading && order && (
        <div className="mobile-content">
      <div className="order-details-container">
        {/* Header do Pedido */}
        <div className="order-details-header">
          <div className="order-code-section">
            <h2>Pedido #{order.order_code || order.id}</h2>
            <div className={`order-status-badge ${getStatusClass(order.status)}`}>
              {getStatusText(order.status)}
            </div>
          </div>
          <div className="order-date">
            <i className="fas fa-calendar"></i>
            {formatDate(order.created_at)}
          </div>
        </div>

        {/* Informações do Cliente */}
        <div className="order-section">
          <h3 className="section-title">
            <i className="fas fa-user"></i> Informações do Cliente
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Nome:</span>
              <span className="info-value">{order.customer_name}</span>
            </div>
            {order.customer_phone && (
              <div className="info-item">
                <span className="info-label">Telefone:</span>
                <span className="info-value">{order.customer_phone}</span>
              </div>
            )}
            <div className="info-item full-width">
              <span className="info-label">Endereço:</span>
              <span className="info-value">{order.customer_address}</span>
            </div>
          </div>
        </div>

        {/* Informações de Pagamento */}
        <div className="order-section">
          <h3 className="section-title">
            <i className="fas fa-credit-card"></i> Pagamento
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Método:</span>
              <span className="info-value">{order.payment_method}</span>
            </div>
            {order.payment_type && (
              <div className="info-item">
                <span className="info-label">Tipo:</span>
                <span className="info-value">{order.payment_type}</span>
              </div>
            )}
            {order.installments && order.installments !== '1' && (
              <div className="info-item">
                <span className="info-label">Parcelas:</span>
                <span className="info-value">{order.installments}x</span>
              </div>
            )}
          </div>
        </div>

        {/* Itens do Pedido */}
        <div className="order-section">
          <h3 className="section-title">
            <i className="fas fa-shopping-bag"></i> Itens do Pedido
          </h3>
          <div className="order-items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => (
                <div key={index} className="order-item-card">
                  <div className="order-item-image">
                    <img
                      src={getMainImage(item.image || (item as any).images)}
                      alt={item.name}
                        onError={(e) => {
                        (e.target as HTMLImageElement).src = '/assets/images/placeholder.jpg';
                      }}
                    />
                  </div>
                  <div className="order-item-info">
                    <div className="order-item-name">{item.name}</div>
                    {(item.color || item.size) && (
                      <div className="order-item-attributes">
                        {item.color && <span>Cor: {item.color}</span>}
                        {item.size && <span>Tamanho: {item.size}</span>}
                      </div>
                    )}
                    <div className="order-item-quantity">
                      Quantidade: {item.quantity}
                    </div>
                    <div className="order-item-price">
                      R$ {parseFloat(String(item.price)).toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                  <div className="order-item-total">
                    R$ {(parseFloat(String(item.price)) * item.quantity).toFixed(2).replace('.', ',')}
                  </div>
                </div>
              ))
            ) : (
              <div>Nenhum item encontrado</div>
            )}
          </div>
        </div>

        {/* Resumo Financeiro */}
        <div className="order-section">
          <h3 className="section-title">
            <i className="fas fa-receipt"></i> Resumo
          </h3>
          <div className="order-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>R$ {parseFloat(String(order.subtotal || 0)).toFixed(2).replace('.', ',')}</span>
            </div>
            {order.shipping_cost > 0 && (
              <div className="summary-row">
                <span>Frete:</span>
                <span>R$ {parseFloat(String(order.shipping_cost)).toFixed(2).replace('.', ',')}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total:</span>
              <span>R$ {parseFloat(String(order.total)).toFixed(2).replace('.', ',')}</span>
            </div>
          </div>
        </div>

        {/* Alterar Status */}
        <div className="order-section">
          <h3 className="section-title">
            <i className="fas fa-edit"></i> Alterar Status
          </h3>
          <div className="status-change-section">
            <label className="form-label" htmlFor="status-select">
              Novo Status
            </label>
            <select
              id="status-select"
              className="form-select"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={updating}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              onClick={handleStatusChange}
              disabled={updating || newStatus === order.status}
              style={{ marginTop: '12px' }}
            >
              {updating ? 'Atualizando...' : 'Atualizar Status'}
            </button>
          </div>
        </div>

        {/* Botão Voltar */}
        <div className="order-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/admin/pedidos')}
          >
            Voltar para Pedidos
          </button>
        </div>
      </div>
    </div>
      )}
    </>
  );
}

export default OrderDetails;

